import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { BankAccount, BankAccountAccessUpdate, BankAccountAssignableUser, BankAccountStatus } from '../models/bank-account/bank-account';
import { BankStatement } from '../models/bank-statement/bank-statement';

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface BankAccountSearchCommand {
  code?: string | null;
  name?: string | null;
  bankName?: string | null;
  bankAccountNumber?: string | null;
  financialCompanyIds?: string[] | null;
  statuses?: number[] | null;
  types?: number[] | null;
  currencies?: number[] | null;
}

@Injectable({ providedIn: 'root' })
export class BankAccountProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/bank-accounts';

  public getAll(): Observable<ApiResponse<BankAccount[]>> {
    return this.httpClient.get<ApiResponse<BankAccount[]>>(`${this.baseUrl}/all`);
  }

  public getById(id: string): Observable<ApiResponse<BankAccount>> {
    return this.httpClient.get<ApiResponse<BankAccount>>(`${this.baseUrl}/${id}`);
  }

  public create(data: BankAccount): Observable<ApiResponse<BankAccount>> {
    return this.httpClient.post<ApiResponse<BankAccount>>(this.baseUrl, data);
  }

  public update(id: string, data: BankAccount): Observable<ApiResponse<BankAccount>> {
    return this.httpClient.put<ApiResponse<BankAccount>>(`${this.baseUrl}/${id}`, data);
  }

  public updateStatus(id: string, status: BankAccountStatus, comment?: string): Observable<ApiResponse<BankAccount>> {
    let params = new HttpParams();
    if (comment) params = params.set('comment', comment);
    return this.httpClient.put<ApiResponse<BankAccount>>(`${this.baseUrl}/status/${id}`, status, { params });
  }

  public delete(id: string): Observable<ApiResponse<unknown>> {
    return this.httpClient.delete<ApiResponse<unknown>>(`${this.baseUrl}/${id}`);
  }

  public search(
    command: BankAccountSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    matchAny = false,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PagedResult<BankAccount>>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('matchAny', matchAny)
      .set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<BankAccount>>>(`${this.baseUrl}/search`, command, { params });
  }

  /**
   * @param includeVoided also return movements that were posted and later voided. Off by default: the
   * bank's own statement never shows one, and a reconciler comparing the two should see the same lines.
   */
  public getStatement(id: string, from: string, to: string, includeVoided = false): Observable<ApiResponse<BankStatement>> {
    const params = new HttpParams().set('from', from).set('to', to).set('includeVoided', includeVoided);
    return this.httpClient.get<ApiResponse<BankStatement>>(`${this.baseUrl}/${id}/statement`, { params });
  }

  /**
   * The statement as an .xlsx workbook.
   *
   * <p><b>`from` and `to` are calendar days (yyyy-MM-dd), deliberately without a time.</b> Posting dates are
   * stored as UTC instants and Honduras runs UTC-6, so 18:30 on the 21st is stored as 00:30Z on the 22nd.
   * The API widens each day to the UTC range it actually spans; sending a timestamp from here instead would
   * hand it a boundary that has already lost that distinction.</p>
   */
  public exportStatement(id: string, from: string, to: string, includeVoided = false): Observable<Blob> {
    const params = new HttpParams().set('from', from).set('to', to).set('includeVoided', includeVoided);
    return this.httpClient.get(`${this.baseUrl}/${id}/statement/export`, { params, responseType: 'blob' });
  }

  /**
   * Sets the account's owner and employee grants together. Owner-only — the API answers 403 otherwise,
   * so callers should gate the UI on `account.isOwner` rather than discovering it here.
   *
   * `employeeAccess` REPLACES the stored list: an omitted employee is a revoked employee.
   */
  public updateAccess(id: string, update: BankAccountAccessUpdate): Observable<ApiResponse<BankAccount>> {
    return this.httpClient.put<ApiResponse<BankAccount>>(`${this.baseUrl}/${id}/access`, update);
  }

  /** Users assignable as owner or employee. Same list the API validates a save against. */
  public getAssignableUsers(): Observable<ApiResponse<BankAccountAssignableUser[]>> {
    return this.httpClient.get<ApiResponse<BankAccountAssignableUser[]>>(`${this.baseUrl}/assignable-users`);
  }
}
